import {
    Component,
    ComponentFactoryResolver,
    Inject, OnDestroy,
    OnInit, ViewChild
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { CoachesProvider, CoachRow } from '../../providers/coaches-provider';
import { AddressesProvider } from '../../../addresses/providers/addresses-provider';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CoachRights } from '../../providers/coaches-rights';
import { GuestsProvider } from '../../../guests/providers/guests-provider';
import { forkJoin, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { Organization } from '../../../common/models/organization';
import { SimpleMatDataSource } from '../../../common/providers/simple-mat-data-source';
import {
  AthleteItemRightsList,
  AthleteRights,
  mapUserRightsToAthleteItemRightsList
} from '../../../athletes/providers/athletes-rights';
import { Coach } from '../../../common/models/coach';
import { CurrentUser } from '../../../common/providers/current-user';
import { AppDatePipe } from '../../../common/pipes/app-date';
import { AthleteRow, AthletesProvider } from '../../../athletes/providers/athletes-provider';
import { TeamsProvider } from '../../../teams/providers/teams-provider';
import { Team } from '../../../common/models/team';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { catchError, map } from "rxjs/operators";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import {
  FeedbackAthleteSmileyComponent
} from "../../../feedback/components/athlete-smiley/feedback-athlete-smiley.component";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatSuffix } from "@angular/material/form-field";
import {
  MatCell,
  MatCellDef, MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatRow,
  MatRowDef,
  MatTable, MatTableModule
} from "@angular/material/table";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { NgForOf, NgIf } from "@angular/common";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { FlexLayoutModule } from "@angular/flex-layout";
import { UsersProvider } from "../../../users/providers/users-provider";
import { OrganizationsProvider } from "../../../organizations/providers/organizations-provider";
import { Athlete } from "../../../common/models/athlete";
import { AthleteTeam } from "../../../common/models/athlete-team";
import { User } from "../../../common/models/user";
import { AthleteCoach } from "../../../common/models/athlete-coach";
import { OrganizationMembership } from "../../../common/models/organization-membership";
import { Guest } from "../../../common/models/guest";
import { AthleteGuest } from "../../../common/models/athlete-guest";
import { Gender } from "../../../common/models/gender";
import { ContractType } from "../../../common/models/contract-type";

@Component({
  selector: 'coachUser-view-page',
  providers: [],
  styleUrls: ['./view.scss'],
  standalone: true,
  imports: [
    ModuleContentComponent,
    Error404Component,
    FeedbackAthleteSmileyComponent,
    PageContentComponent,
    Error401Component,
    MatIcon,
    MatFormField,
    MatTableModule,
    TranslateModule,
    HeaderComponent,
    MatSortModule,
    NgIf,
    NgForOf,
    RouterLink,
    MatButtonModule,
    MatInput,
    FlexLayoutModule,
    MatSuffix
  ],
  templateUrl: './view.html'
})
export class CoachViewPageComponent implements OnInit, OnDestroy {

    public pageTitle: string |null= null;
    public isLoading = true;
    public is401 = false;
    public is404 = false;
    public organization: Organization = {} as Organization;
    public viewedCoach: CoachRow = {} as CoachRow;

    public columns: string[] = ['avatar', 'name', 'teams', 'filling', 'actions'];
    public dataAthleteSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public rights: CoachRights = new CoachRights(this._utils);
    public moodIconClass = 'athlete-smiley-icon medium-smiley material-icons';

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator|null = null;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort|null = null;
    private athletesRights: AthleteRights = new AthleteRights(this._utils);
    private coachUser: User = {} as User;

    private crumb: string |null= null;
    private backUrl: string |null= null;
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        private _utils: UtilsProvider,
        private _title: Title,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _currentUser: CurrentUser,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _coachesProvider: CoachesProvider,
        private _athletesProvider: AthletesProvider,
        private _organizationProvider: OrganizationsProvider,
        private _usersProvider: UsersProvider,
        private _router: Router,
        private _teamsProvider: TeamsProvider,
        private _guestsProvider: GuestsProvider,
    ) {
    }

    public ngOnInit() {
        this.crumb = this._route.snapshot.data['crumb'];
        this.backUrl = this._route.snapshot.data['backUrl'];

        this.subscriptions.push(
            this._route.data.subscribe((data: any) => { //{ organization: Organization; coachUser: Coach }
                this.organization = data.organization;
                this.coachUser = data.coach;
                this.initPage();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onFilterKeyup(filter: string): void {
        this.dataAthleteSource.filter = this._utils.normalizeString(filter);
    }

    private initPage(): void {
      this.loadData();
      this.initNav();
      this.initSearch();
      this.isLoading = false;
    }

    private initNav(): void {
        if (!this.viewedCoach?.rights?.view) {
            this.is401 = true;
            return;
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    private initSearch(): void {
        this.dataAthleteSource.sort = this.sorter;
        this.dataAthleteSource.filterPredicate = (data: AthleteRow, filter: string) => {
            const str: string = this._utils.normalizeString(`${data.user.firstName} ${data.user.lastName}`);
            return str.includes(filter);
        };
    }

    public onDisplayTeamClicked(toView: Team): void {

        const url = '/' + this.organization?.code + '/teams/' + toView.id;
        this._router.navigateByUrl(url);
    }

  private loadData(): void {
    if (!this.organization?.id || !this.coachUser?.id) {
      console.error('Organization or coach not found');
      this.onError({ error: 'Organization or coach not found', status: 404 });
      return;
    }

    this.viewedCoach = this._coachesProvider.createCoachRow(this.organization.id, this.coachUser);
    this.viewedCoach.teams.sort((a, b) => this._utils.sortRow(a.name.toLowerCase(), b.name.toLowerCase()));

    const athletesRows: AthleteRow[] = this.coachUser.coach?.athletes?.map((athlete : Athlete) => {
      return {
        user: athlete.user!,
        athlete: athlete,
        teams: athlete.teams ?? [],
        teamNames: athlete.teams?.map((team: Team) => team.name).join(', ') ?? '',
        rights: mapUserRightsToAthleteItemRightsList(this.coachUser.rights ?? []),
        gender: {} as Gender,
        contractType: {} as ContractType,
        addresses: [],
        coaches: [],
        coachNames: ''
      };
    }) ?? [];
    this.dataAthleteSource.replaceAllWith(athletesRows);
    this.dataAthleteSource.addSortDefinition(
      'name',
      (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${a.user.firstName} ${a.user.lastName}`).toLowerCase(), (`${b.user.firstName} ${b.user.lastName}`).toLowerCase()),
      (a: AthleteRow, b: AthleteRow) => this._utils.sortRow((`${b.user.firstName} ${b.user.lastName}`).toLowerCase(), (`${a.user.firstName} ${a.user.lastName}`).toLowerCase())
    );
    this.dataAthleteSource.sortBy('name', 'asc');

    this.rights.evaluateScreenRights({
      rolesIds: this._currentUserRoles.getRolesIds(this.organization.id)
    });
  }


  private createPageTitle() {
      this.pageTitle = this.viewedCoach?.user.firstName + ' ' + this.viewedCoach?.user.lastName + ' | ' + this._translater.instant(this.crumb!);
      this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
  }

    private createBreadcrumb() {
        const crumb: Crumb[] = [];
        if (this.rights.LIST) {
            crumb.push({
                shortTitle: this.crumb!,
                title: this.crumb!,
                url: '/' + this.organization!.code + this.backUrl
            });
        }
        crumb.push({
            shortTitle: this.viewedCoach?.user.firstName + ' ' + this.viewedCoach?.user.lastName,
            title: this.viewedCoach?.user.firstName + ' ' + this.viewedCoach?.user.lastName,
            url: '/' + this.organization?.code + this.backUrl + '/' + this.viewedCoach?.coach.id
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumb);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        } else {
          console.error("Coach : ", error);
        }
    }
}
